import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { TutorialVideoDialogComponent } from './tutorial-video-dialog/tutorial-video-dialog.component';
import {
  SERASA_CASE_CODE,
  TUTORIAL_DIALOG_CONFIG,
} from './tutorial-video.constants';
import { TutorialVideoService } from './tutorial-video.service';

interface WelcomeBannerConfig {
  enabled: boolean;
  size: {
    width: string;
    maxWidth: string;
  };
  theme: {
    primaryColor: string;
    secondaryColor: string;
    headerTextColor: string;
    contentTextColor: string;
    buttonTextColor: string;
  };
  header: {
    imageUrl: {
      pt?: string;
      en?: string;
    };
    descriptionHtml: {
      pt?: string;
      en?: string;
    };
  };
  content: {
    title: {
      pt?: string;
      en?: string;
    };
    descriptionHtml: {
      pt?: string;
      en?: string;
    };
  };
  cta: {
    label: {
      pt?: string;
      en?: string;
    };
  };
}

@Component({
  selector: 'app-welcome-popup',
  templateUrl: './welcome-popup.component.html',
  styleUrls: ['./welcome-popup.component.scss'],
})
export class WelcomePopupComponent {
  bannerConfig?: WelcomeBannerConfig;
  hasBannerConfig = false;
  currentLang: string = 'pt';

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contract_id?: string;
      claim_id?: string;
      bannerConfig?: WelcomeBannerConfig;
      caseId?: string;
      caseCode?: string;
    },
    public dialog: MatDialogRef<WelcomePopupComponent>,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private matDialog: MatDialog,
    private videoService: TutorialVideoService
  ) {
    this.bannerConfig = data.bannerConfig;
    this.hasBannerConfig = !!data.bannerConfig;
    this.currentLang =
      this.translate.currentLang || this.translate.defaultLang || 'pt';
  }

  get hasTutorialVideo(): boolean {
    return this.data.caseCode === SERASA_CASE_CODE;
  }

  async openTutorialVideo(): Promise<void> {
    this.dialog.close();

    if (this.videoService.isInPip) {
      await this.videoService.exitPip();
    }

    this.matDialog.open(TutorialVideoDialogComponent, TUTORIAL_DIALOG_CONFIG);
  }

  getHeaderDescriptionHtml(): SafeHtml {
    const html = this.getLocalizedContent(
      this.bannerConfig?.header?.descriptionHtml
    );
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getContentDescriptionHtml(): SafeHtml {
    const html = this.getLocalizedContent(
      this.bannerConfig?.content?.descriptionHtml
    );
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  getContentTitle(): string {
    return this.getLocalizedContent(this.bannerConfig?.content?.title) || '';
  }

  getCtaLabel(): string {
    return this.getLocalizedContent(this.bannerConfig?.cta?.label) || '';
  }

  getHeaderImageUrl(): string {
    return this.getLocalizedContent(this.bannerConfig?.header?.imageUrl) || '';
  }

  private getLocalizedContent(
    content: string | { pt?: string; en?: string } | undefined
  ): string {
    if (!content) {
      return '';
    }

    if (typeof content === 'string') {
      return content;
    }

    const lang = this.currentLang as 'pt' | 'en';
    return content[lang] || content.pt || content.en || '';
  }
}
